import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowDownToLine, Trash2 } from "lucide-react";
import { Spinner } from "@/components/ui/Spinner";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { logger } from "@/services/LoggingService";

const COMPONENT = 'AssetDocumentItem';

interface AssetDocumentItemProps {
  id: string;
  fileName: string;
  filePath: string;
  displayTitle: string;
  canManageDocuments: boolean;
  onDelete: (docId: string) => void;
}

export function AssetDocumentItem({ 
  id, 
  fileName, 
  filePath, 
  displayTitle,
  canManageDocuments,
  onDelete 
}: AssetDocumentItemProps) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState(displayTitle);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async () => {
    try {
      setIsDownloading(true);
      const { data, error } = await supabase.storage
        .from('asset_documents')
        .download(filePath);

      if (error) throw error;

      const url = window.URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      logger.error(COMPONENT, 'Download error:', { error, fileName, filePath });
      toast.error("There was an error downloading the document.");
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      const { error: storageError } = await supabase.storage
        .from('asset_documents')
        .remove([filePath]);

      if (storageError) throw storageError;

      const { error: dbError } = await supabase
        .from('asset_documents')
        .delete()
        .eq('id', id);

      if (dbError) throw dbError;

      toast.success("The document has been successfully deleted.");
      onDelete(id);
    } catch (error) {
      logger.error(COMPONENT, 'Delete error:', { error, fileName, filePath });
      toast.error("There was an error deleting the document.");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleEditSave = async () => {
    try {
      const { error } = await supabase
        .from('asset_documents')
        .update({ display_title: editValue })
        .eq('id', id);

      if (error) throw error;

      setEditingId(null);
      toast.success("Document title has been updated successfully.");
    } catch (error) {
      logger.error(COMPONENT, 'Update error:', { error, fileName });
      toast.error("Failed to update document title.");
      setEditValue(displayTitle);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleEditSave();
    } else if (e.key === 'Escape') {
      setEditingId(null);
      setEditValue(displayTitle);
    }
  };

  return (
    <div className="flex flex-col p-3 bg-muted rounded-lg overflow-hidden">
      <div className="flex items-center justify-between">
        {canManageDocuments && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDelete}
            disabled={isDeleting}
            className="mr-2 h-8 w-8 p-0"
          >
            {isDeleting ? (
              <Spinner size="sm" className="text-muted-foreground" ariaLabel="Deleting" />
            ) : (
              <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" />
            )}
          </Button>
        )}
        <div className="flex-1 mr-4">
          {editingId === id && canManageDocuments ? (
            <Input
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onBlur={handleEditSave}
              onKeyDown={handleKeyPress}
              autoFocus
              className="w-full"
            />
          ) : (
            <div 
              onClick={() => canManageDocuments && setEditingId(id)}
              className={canManageDocuments ? "cursor-pointer hover:text-primary" : ""}
            >
              <span className="text-base font-medium block">{displayTitle}</span>
              {canManageDocuments && (
                <span className="text-xs text-muted-foreground block">{fileName}</span>
              )}
            </div>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleDownload}
          disabled={isDownloading}
          className="shrink-0"
        >
          {isDownloading ? (
            <Spinner size="sm" className="mr-2" ariaLabel="Downloading" />
          ) : (
            <ArrowDownToLine className="h-4 w-4 mr-2" />
          )}
          Download
        </Button>
      </div>
    </div>
  );
}
